import { useState, useEffect } from 'react';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', coral: '#FF6B6B', text: '#E8EDF5', sub: '#C5CDD8',
};

const API = `${import.meta.env.VITE_API_URL || 'http://localhost:3000/api'}/desktop`;

function formatBytes(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function Download() {
  const [release, setRelease]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    fetch(`${API}/latest`)
      .then((r) => {
        if (!r.ok) throw new Error('Failed to fetch release info');
        return r.json();
      })
      .then((data) => setRelease(data.available ? data : null))
      .catch((err) => {
        console.error('[Download] fetch release error:', err);
        setError('Failed to fetch release info. Please try again.');
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: C.ink,
      color: C.text,
      fontFamily: "'Cabinet Grotesk', sans-serif",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
    }}>
      {/* Logo */}
      <div style={{ marginBottom: 40, textAlign: 'center' }}>
        <div style={{ fontSize: '2.4rem', fontWeight: 800, letterSpacing: '-1px' }}>
          Opti<span style={{ color: C.gold }}>Ka</span>
        </div>
        <div style={{ fontSize: '0.8rem', color: C.muted, letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: 4 }}>
          Business Intelligence
        </div>
      </div>

      {/* Card */}
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 20,
        padding: '40px 48px',
        maxWidth: 520,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
      }}>
        {/* Windows icon */}
        <div style={{
          width: 72, height: 72, borderRadius: 18,
          background: 'linear-gradient(135deg, #0078D4, #00B4D8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px', fontSize: '2rem',
        }}>
          <svg viewBox="0 0 24 24" width="36" height="36" fill="white">
            <path d="M3 5.5L11 4v8H3V5.5zM12 3.85L21 2.5v9.5h-9V3.85zM3 12.5h8V20L3 18.5V12.5zM12 12.5h9v9.5L12 20.5V12.5z"/>
          </svg>
        </div>

        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, margin: '0 0 8px', color: C.text }}>
          OptiKa for Windows
        </h1>
        <p style={{ color: C.muted, fontSize: '0.9rem', margin: '0 0 32px', lineHeight: 1.6 }}>
          The full OptiKa experience as a native desktop app. Keyboard shortcuts, system tray, auto-updates.
        </p>

        {loading && (
          <div style={{ color: C.muted, fontSize: '0.9rem', padding: '16px 0' }}>
            Checking for latest version...
          </div>
        )}

        {error && (
          <div style={{ color: C.coral, fontSize: '0.85rem', padding: '16px 0' }}>
            Could not fetch latest release. Try again later.
          </div>
        )}

        {!loading && !error && !release && (
          <div style={{ color: C.muted, fontSize: '0.85rem', padding: '16px 0' }}>
            No release available yet. Check back soon.
          </div>
        )}

        {release && (
          <>
            <a
              href={`${API}/download`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                background: C.gold, color: C.ink,
                fontWeight: 700, fontSize: '1rem',
                padding: '14px 32px', borderRadius: 12,
                textDecoration: 'none',
                transition: 'background 0.15s',
                marginBottom: 16,
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#FFCD3C'}
              onMouseOut={(e) => e.currentTarget.style.background = C.gold}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download Installer
            </a>

            <div style={{ color: C.muted, fontSize: '0.78rem', lineHeight: 1.8 }}>
              <div>
                <span style={{ color: C.muted }}>Version</span>{' '}
                <span style={{ color: C.sub, fontWeight: 600 }}>{release.version}</span>
                {'  ·  '}
                <span style={{ color: C.muted }}>Size</span>{' '}
                <span style={{ color: C.sub }}>{formatBytes(release.size)}</span>
              </div>
              <div style={{ marginTop: 2 }}>
                Released {formatDate(release.publishedAt)}
              </div>
            </div>
          </>
        )}

        {/* System requirements */}
        <div style={{
          marginTop: 32,
          paddingTop: 24,
          borderTop: `1px solid ${C.border}`,
          fontSize: '0.75rem',
          color: C.muted,
          lineHeight: 1.8,
        }}>
          <div style={{ fontWeight: 600, color: C.muted, marginBottom: 6 }}>SYSTEM REQUIREMENTS</div>
          Windows 10 or later · 64-bit · 200 MB disk space · Internet connection required
        </div>
      </div>

      {/* Features strip */}
      <div style={{
        display: 'flex', gap: 32, marginTop: 40, flexWrap: 'wrap', justifyContent: 'center',
        fontSize: '0.8rem', color: C.muted,
      }}>
        {[
          ['⌨️', 'Keyboard shortcuts'],
          ['🔔', 'System tray'],
          ['🔄', 'Auto-updates'],
          ['📡', 'Offline fallback'],
        ].map(([icon, label]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{icon}</span><span>{label}</span>
          </div>
        ))}
      </div>

      {/* Back link */}
      <a
        href="/"
        style={{ marginTop: 40, color: C.muted, fontSize: '0.8rem', textDecoration: 'none' }}
        onMouseOver={(e) => e.currentTarget.style.color = C.sub}
        onMouseOut={(e) => e.currentTarget.style.color = C.muted}
      >
        ← Back to OptiKa
      </a>
    </div>
  );
}
